import { withAuthRecovery } from '@/lib/authRecovery'
import { getSupabase } from '@/lib/supabase'

// ============================================================================
// Смена имени участника. Две операции с РАЗНЫМИ точками входа:
//   * юридическое ФИО — только юристы, карточка ДКК в реестре документов;
//   * форма имени в списках — УК, /management/original.
// Полная модель и радиус — docs/PARTY_IDENTITY_CHANGE_PLAN.md.
// ============================================================================

export type PartyIdentityLegalEntity = {
  entity_id: number
  partner_number: number
  inn: string | null
  legal_name: string
  is_primary: boolean
  person_id: number | null
  linked_to_person: boolean
  /** IP — ИНН 12 цифр (ФИО в названии), ORG — 10, у него фамилии нет по построению. */
  kind: 'IP' | 'ORG' | 'NO_INN' | 'OTHER'
  lastname_in_name: boolean
}

export type PartyIdentityAppUser = {
  user_id: string
  email: string | null
  telegram_id: number | null
  first_name: string | null
  last_name: string | null
  /** Связи users ↔ persons в модели нет — это КАНДИДАТ по совпадению ФИО, а не факт. */
  match_kind: 'exact' | 'lastname'
}

export type PartyIdentityPreview = {
  actor_user_id: string
  person: {
    person_id: number
    first_name: string
    last_name: string
    display_name: string | null
  }
  partner_numbers: number[]
  legal_entities: PartyIdentityLegalEntity[]
  app_users: PartyIdentityAppUser[]
  shmp_rows: Array<{ id: number; partner_number: number; partner_name: string | null; city_name: string | null }>
  rating_rows: { daily: number; weekly: number; period: number }
  /** Внешний источник: меняется только в ListokCRM самим франчайзи. */
  crm_accounts: Array<{ account: string; city_name: string | null; account_fio: string | null }>
  /** Архив: не меняется никогда. Показывается, чтобы юрист видел границу операции. */
  snapshots: { payments: number; bank_staging: number }
  contracts: Array<{ contract_id: number; contract_number: string | null; series_id: number; is_active: boolean }>
}

export type PartyIdentityChange = {
  change_id: number
  change_kind: 'LEGAL_NAME' | 'DISPLAY_NAME' | 'ENTITY_PERSON_LINK'
  person_id: number | null
  partner_number: number | null
  entity_ids: number[]
  target_user_id: string | null
  effective_from: string | null
  document_kind: string | null
  document_number: string | null
  doc_url: string | null
  note: string | null
  old_value: Record<string, unknown>
  new_value: Record<string, unknown>
  amendment_ids: number[]
  actor_user_id: string | null
  actor_label: string | null
  created_at: string
}

export type LegalNameChangeResult = {
  change_id: number
  person_id: number
  old_first_name: string
  old_last_name: string
  new_first_name: string
  new_last_name: string
  partner_numbers: number[]
  entity_ids: number[]
  amendment_ids: number[]
  shmp_rows_updated: number
  rating_rows_updated: number
}

export type DisplayNameChangeResult = {
  change_id: number
  person_id: number
  old_display_name: string | null
  new_display_name: string | null
  app_user_id: string | null
  shmp_rows_updated: number
  rating_rows_updated: number
}

export type ContractNameChangePreview = {
  person: {
    person_id: number
    first_name: string
    last_name: string
    display_name: string | null
  }
  contracts: Array<{
    contract_id: number
    contract_number: string | null
    is_active: boolean
    entity_id: number | null
    legal_name: string | null
    inn: string | null
  }>
}

export type ContractNameChangeResult = LegalNameChangeResult & {
  signed_at: string
  effective_from: string
}

/** Компактные данные для смены имени: персона и её договоры, без тяжёлого расчёта радиуса. */
export async function fetchContractNameChangePreview(personId: number): Promise<ContractNameChangePreview> {
  const { data, error } = (await withAuthRecovery(() =>
    (getSupabase() as any).rpc('get_contract_name_change_preview', { p_person_id: personId })
  )) as { data: ContractNameChangePreview | null; error: any }

  if (error) throw error
  if (!data) throw new Error('Не удалось загрузить договоры')
  return data
}

/**
 * Единая договорная операция: меняет ФИО, создаёт ДС по выбранным договорам и
 * синхронизирует производные имена. Причина и документ-основание здесь не нужны.
 */
export async function applyContractNameChange(params: {
  personId: number
  newFirstName: string
  newLastName: string
  signedAt: string
  effectiveFrom: string
  contractIds: number[]
}): Promise<ContractNameChangeResult> {
  const { data, error } = (await withAuthRecovery(() =>
    (getSupabase() as any).rpc('apply_contract_name_change', {
      p_person_id: params.personId,
      p_new_first_name: params.newFirstName.trim(),
      p_new_last_name: params.newLastName.trim(),
      p_signed_at: params.signedAt,
      p_effective_from: params.effectiveFrom,
      p_contract_ids: params.contractIds,
    })
  )) as { data: ContractNameChangeResult | null; error: any }

  if (error) throw error
  if (!data) throw new Error('Смена имени не проведена')
  return data
}

export async function fetchPartyIdentityPreview(personId: number): Promise<PartyIdentityPreview> {
  const { data, error } = (await withAuthRecovery(() =>
    (getSupabase() as any).rpc('get_party_identity_preview', { p_person_id: personId })
  )) as { data: PartyIdentityPreview | null; error: any }

  if (error) throw error
  if (!data) throw new Error('Не удалось собрать радиус изменения')
  return data
}

/** Операция УК: форма имени в списках. Юридического ФИО и юрлиц не касается. */
export async function applyDisplayNameChange(params: {
  personId: number
  displayName: string | null
  userId?: string | null
  userFirstName?: string | null
  userLastName?: string | null
  note?: string | null
}): Promise<DisplayNameChangeResult> {
  const { data, error } = (await withAuthRecovery(() =>
    (getSupabase() as any).rpc('apply_display_name_change', {
      p_person_id: params.personId,
      p_display_name: params.displayName?.trim() || null,
      p_user_id: params.userId ?? null,
      p_user_first_name: params.userFirstName?.trim() || null,
      p_user_last_name: params.userLastName?.trim() || null,
      p_note: params.note?.trim() || null,
    })
  )) as { data: DisplayNameChangeResult | null; error: any }

  if (error) throw error
  if (!data) throw new Error('Форма имени не изменена')
  return data
}

/** Привязка «на кого оформлено это ИП». null — юрлицо не персонифицировано. */
export async function setLegalEntityPerson(params: {
  entityId: number
  personId: number | null
  note?: string | null
}): Promise<{ change_id: number; entity_id: number; old_person_id: number | null; new_person_id: number | null }> {
  const { data, error } = (await withAuthRecovery(() =>
    (getSupabase() as any).rpc('set_legal_entity_person', {
      p_entity_id: params.entityId,
      p_person_id: params.personId,
      p_note: params.note?.trim() || null,
    })
  )) as { data: any; error: any }

  if (error) throw error
  return data
}

export async function listPartyIdentityChanges(params: {
  personId?: number | null
  partnerNumber?: number | null
  limit?: number
}): Promise<PartyIdentityChange[]> {
  const { data, error } = (await withAuthRecovery(() =>
    (getSupabase() as any).rpc('list_party_identity_changes', {
      p_person_id: params.personId ?? null,
      p_partner_number: params.partnerNumber ?? null,
      p_limit: params.limit ?? 100,
    })
  )) as { data: PartyIdentityChange[] | null; error: any }

  if (error) throw error
  return data ?? []
}

export type UnlinkedLegalEntity = {
  entity_id: number
  partner_number: number
  inn: string | null
  legal_name: string
  is_primary: boolean
  entity_kind: 'IP' | 'ORG' | 'NO_INN' | 'OTHER'
  partner_persons: Array<{
    person_id: number
    first_name: string
    last_name: string
    display_name: string | null
    /** Фамилия персоны встречается в названии юрлица. */
    lastname_in_name: boolean
  }>
  /** Имя плательщика из последней банковской выписки — факт на момент операции. */
  bank_last_payer_name: string | null
  bank_last_payment_date: string | null
  bank_payment_count: number
  lastname_matches_person: boolean
}

/** Юрлица без проставленного владельца — экран `/management/legal-entities`. */
export async function listUnlinkedLegalEntities(): Promise<UnlinkedLegalEntity[]> {
  const { data, error } = (await withAuthRecovery(() =>
    (getSupabase() as any).rpc('list_unlinked_legal_entities')
  )) as { data: UnlinkedLegalEntity[] | null; error: any }

  if (error) throw error
  return data ?? []
}

/**
 * Привязывает аккаунт приложения к персоне. Одна персона — один аккаунт (UNIQUE).
 * `syncName: true` сразу приводит имя аккаунта к форме имени персоны.
 */
export async function setPersonAppUser(params: {
  personId: number
  userId: string | null
  syncName?: boolean
  note?: string | null
}): Promise<{ change_id: number; old_user_id: string | null; new_user_id: string | null; name_synced: boolean }> {
  const { data, error } = (await withAuthRecovery(() =>
    (getSupabase() as any).rpc('set_person_app_user', {
      p_person_id: params.personId,
      p_user_id: params.userId,
      p_sync_name: params.syncName ?? true,
      p_note: params.note?.trim() || null,
    })
  )) as { data: any; error: any }

  if (error) throw error
  return data
}
